import { useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/core/api/client';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Textarea } from '@/shared/components/ui/textarea';
import type { FinancialAdvisorProfile } from '@/features/provider-directory/types/financial-advisor-profile.types';

type AdvisorProfileUpdateRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  profile: FinancialAdvisorProfile;
};

export const AdvisorProfileUpdateRequestModal = ({
  isOpen,
  onClose,
  profile,
}: AdvisorProfileUpdateRequestModalProps) => {
  const [updateRequest, setUpdateRequest] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleClose = () => {
    if (isSubmitting) return;
    setUpdateRequest('');
    setSubmitStatus('idle');
    setErrorMessage('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!updateRequest.trim()) {
      setErrorMessage('Please describe the updates you would like to make.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSubmitStatus('idle');

    try {
      await apiClient.post('/providers/profile-update-request', {
        providerId: profile.id,
        providerName: profile.companyName || `${profile.user.firstName} ${profile.user.lastName ?? ''}`.trim(),
        providerEmail: profile.user.email ?? '',
        updateRequest: updateRequest.trim(),
      });
      setUpdateRequest('');
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error submitting advisor profile update request:', error);
      setSubmitStatus('error');
      setErrorMessage('Failed to submit your request. Please try again or contact support directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Request Profile Updates</DialogTitle>
          <DialogDescription>
            Tell us what admin-only profile details need to change.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {submitStatus === 'success' ? (
            <Alert className="bg-emerald-50 border-emerald-200">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800">
                Your request has been submitted successfully.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Textarea
                value={updateRequest}
                onChange={(event) => setUpdateRequest(event.target.value)}
                rows={6}
                className="resize-none"
                disabled={isSubmitting}
                placeholder="Please describe the updates you would like to make to admin-only fields..."
              />
              {errorMessage && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {submitStatus !== 'success' && (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
